import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ManagedImage } from '../components/ManagedImage';
import { Lightbox } from '../components/Lightbox';
import { fetchGallery, fetchSiteImages } from '../lib/portfolio';
import { fetchReviews, fallbackReviews, reviewInitials } from '../lib/reviews';
import { fetchSiteTexts, fallbackTexts } from '../lib/texts';
import { buildFallbackGallery, buildFallbackSiteImages } from '../lib/fallback';

const services = [
  { slug: 'mobelrenovering', slot: 'thumb_mobelrenovering', title: 'Möbelrenovering', description: 'Jag förvandlar gamla favoriter till nya älsklingar – med varsam renovering, nytänk och kreativt hantverk.' },
  { slug: 'dukning', slot: 'thumb_dukning', title: 'Bröllop & Fest', description: 'Personligt anpassade arrangemang för livets viktigaste tillfällen – oavsett om det gäller babyshower, en mindre fest eller ett stort bröllop.' },
  { slug: 'skyltfonster', slot: 'thumb_skyltfonster', title: 'Skyltfönster & Butik', description: 'Jag hjälper dig att skapa skyltfönster och butiksexponering som både inspirerar och säljer – med känsla för helheten och öga för detaljer.' },
  { slug: 'design', slot: 'thumb_design', title: 'Grafisk Design', description: 'Jag hjälper dig att ta fram en personlig logotyp som bygger på dina idéer och speglar din verksamhet.' },
];

const stars = (n) => '★★★★★'.slice(0, Math.max(0, Math.min(5, n || 5)));

const SectionLabel = ({ children }) => (
  <span className="text-xs tracking-[0.34em] uppercase text-cv-accent">{children}</span>
);

const Slot = ({ img, className, style }) =>
  img ? (
    <ManagedImage
      url={img.url}
      basePath={img.basePath}
      filename={img.filename}
      alt={img.alt}
      fit={img.fit}
      focusX={img.focusX}
      focusY={img.focusY}
      className={className}
      style={style}
    />
  ) : null;

export const HomePage = () => {
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(buildFallbackGallery);
  const [siteImages, setSiteImages] = useState(buildFallbackSiteImages);
  const [reviews, setReviews] = useState(fallbackReviews);
  const [texts, setTexts] = useState(fallbackTexts);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    let mounted = true;
    fetchGallery().then((imgs) => mounted && setGallery(imgs));
    fetchSiteImages().then((imgs) => mounted && setSiteImages(imgs));
    fetchReviews().then((r) => mounted && setReviews(r));
    fetchSiteTexts().then((t) => mounted && setTexts(t));
    return () => {
      mounted = false;
    };
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-cv-base">
      {/* ============ HERO ============ */}
      <section style={{ background: 'linear-gradient(180deg, #f3eee6 0%, #faf7f2 100%)', padding: 'clamp(130px,20vw,160px) clamp(20px,5vw,32px) clamp(64px,10vw,96px)' }}>
        <div className="max-w-[1200px] mx-auto grid items-center" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,340px),1fr))', gap: 'clamp(40px,6vw,72px)' }}>
          <div className="flex flex-col gap-7">
            <div className="flex items-center gap-3.5">
              <span className="w-10 h-px bg-cv-accent" />
              <SectionLabel>Hedemora · Dalarna</SectionLabel>
            </div>
            <h1 className="m-0 font-serif font-normal text-cv-ink" style={{ fontSize: 'clamp(36px,6vw,58px)', lineHeight: 1.12, textWrap: 'balance' }}>
              {texts.hero_title} {texts.hero_title_accent && <em className="text-cv-accent">{texts.hero_title_accent}</em>}
            </h1>
            <p className="m-0 text-[17px] leading-[1.7] text-cv-muted max-w-[46ch]">
              {texts.hero_subtitle}
            </p>
            <div className="flex flex-wrap gap-4 mt-2">
              <button onClick={() => scrollTo('services-section')} className="inline-flex items-center gap-3 bg-cv-ink text-cv-base border-none cursor-pointer px-[34px] py-4 text-[13px] tracking-[0.18em] uppercase rounded-cv hover:bg-cv-ink2 transition-colors">
                Utforska Portfolio <span className="font-serif">→</span>
              </button>
              <button onClick={() => scrollTo('reviews-section')} className="inline-flex items-center bg-transparent text-cv-ink border border-cv-edge cursor-pointer px-[34px] py-4 text-[13px] tracking-[0.18em] uppercase rounded-cv hover:bg-cv-line transition-colors">
                Läs omdömen
              </button>
            </div>
          </div>

          <div className="relative" style={{ height: 'clamp(340px,80vw,560px)' }}>
            <div className="absolute top-0 right-0 overflow-hidden" style={{ width: '72%', height: '76%', borderRadius: 'var(--cv-radius)', boxShadow: '0 24px 60px rgba(46,42,36,0.18)' }}>
              <Slot img={siteImages.hero_primary} className="w-full h-full" />
            </div>
            <div className="absolute bottom-0 left-0 overflow-hidden" style={{ width: '52%', height: '52%', borderRadius: 'var(--cv-radius)', border: '6px solid #faf7f2', boxShadow: '0 18px 44px rgba(46,42,36,0.16)' }}>
              <Slot img={siteImages.hero_secondary} className="w-full h-full" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ TJÄNSTER ============ */}
      <section id="services-section" className="bg-cv-base" style={{ padding: 'clamp(64px,10vw,96px) clamp(20px,5vw,32px)' }}>
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-16 flex flex-col items-center gap-[18px]">
            <SectionLabel>Portfolio</SectionLabel>
            <h2 className="m-0 font-serif font-medium text-cv-ink" style={{ fontSize: 'clamp(30px,5vw,42px)' }}>Mina tjänster</h2>
            <span className="w-14 h-px bg-cv-accent" />
            <p className="m-0 text-base leading-[1.7] text-cv-muted max-w-[62ch]">
              Jag hjälper dig att ge gamla möbler nytt liv, skapa minnesvärda bröllop och fester, samt erbjuder hjälp med skyltfönster och logotyper som speglar ditt varumärke.
            </p>
          </div>

          <div className="grid gap-10" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,340px),1fr))' }}>
            {services.map((s) => (
              <div key={s.slug} onClick={() => navigate(`/tjanst/${s.slug}`)} className="cursor-pointer flex flex-col gap-[18px] group">
                <div className="overflow-hidden bg-cv-panel" style={{ aspectRatio: '4/3', borderRadius: 'var(--cv-radius)' }}>
                  <Slot img={siteImages[s.slot]} className="w-full h-full transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="m-0 font-serif font-medium text-[26px] text-cv-ink">{s.title}</h3>
                  <p className="m-0 text-[15px] leading-[1.65] text-cv-muted">{s.description}</p>
                  <span className="inline-flex items-center gap-2 mt-1.5 text-[13px] tracking-[0.14em] uppercase text-cv-accent border-b border-cv-edge2 pb-1 w-fit">
                    Se projekt <span className="font-serif">→</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ OMDÖMEN ============ */}
      <section id="reviews-section" className="bg-cv-panel" style={{ padding: 'clamp(64px,10vw,96px) clamp(20px,5vw,32px)' }}>
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-14 flex flex-col items-center gap-[18px]">
            <SectionLabel>Omdömen</SectionLabel>
            <h2 className="m-0 font-serif font-medium text-cv-ink" style={{ fontSize: 'clamp(30px,5vw,42px)' }}>Kundomdömen</h2>
            <span className="w-14 h-px bg-cv-accent" />
            <p className="m-0 text-base text-cv-muted">Läs vad mina kunder säger om sina upplevelser</p>
          </div>

          <div className="grid gap-8 mb-12" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))' }}>
            {reviews.map((r) => {
              const initials = reviewInitials(r.name);
              return (
                <figure key={r.id} className="m-0 bg-cv-base border border-cv-border p-10 flex flex-col gap-5" style={{ borderRadius: 'var(--cv-radius)' }}>
                  <span className="font-serif text-cv-accent mt-4" style={{ fontSize: 64, lineHeight: 0.5 }}>“</span>
                  <blockquote className="m-0 font-serif italic text-[19px] leading-[1.6] text-cv-ink2">{r.quote}</blockquote>
                  <figcaption className="flex items-center gap-4 mt-auto pt-5 border-t border-cv-border">
                    <span className="w-11 h-11 rounded-full border border-cv-accent flex items-center justify-center font-serif text-cv-accent" style={{ fontSize: initials.length > 1 ? 15 : 18 }}>{initials}</span>
                    <span className="flex flex-col gap-0.5">
                      <span className="font-normal text-[15px] text-cv-ink">{r.name}</span>
                      <span className="text-[13px] text-cv-faint tracking-[0.08em]">{stars(r.rating)} {r.tag && <>&nbsp;{r.tag}</>}</span>
                    </span>
                  </figcaption>
                </figure>
              );
            })}
          </div>

          <div className="text-center flex flex-col items-center gap-3">
            <p className="m-0 text-[15px] text-cv-muted">Har du anlitat CHills Visuella?</p>
            <a href="mailto:charlott@hillservice.se?subject=Omdöme" className="inline-flex items-center gap-2.5 text-[13px] tracking-[0.16em] uppercase text-cv-ink no-underline border-b border-cv-edge pb-1 hover:border-cv-ink transition-colors">
              Lämna ditt omdöme <span className="font-serif">→</span>
            </a>
          </div>
        </div>
      </section>

      {/* ============ GALLERI (ersätter Instagram-widget) ============ */}
      <section className="bg-cv-base" style={{ padding: 'clamp(64px,10vw,96px) clamp(20px,5vw,32px) 104px' }}>
        <div className="max-w-[1100px] mx-auto">
          <div className="text-center mb-14 flex flex-col items-center gap-[18px]">
            <SectionLabel>Galleri</SectionLabel>
            <h2 className="m-0 font-serif font-medium text-cv-ink" style={{ fontSize: 'clamp(30px,5vw,42px)' }}>Senaste projekt</h2>
            <span className="w-14 h-px bg-cv-accent" />
            <p className="m-0 text-base leading-[1.7] text-cv-muted max-w-[52ch]">Följ vår kreativa process och se de senaste transformationerna</p>
          </div>

          <div className="grid gap-3.5" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%, max(150px, 22%)),1fr))', gridAutoRows: 'clamp(140px,22vw,180px)' }}>
            {gallery.map((g, i) => (
              <div key={g.id} onClick={() => setLightboxIndex(i)} className="overflow-hidden cursor-pointer bg-cv-panel group" style={{ gridRow: `span ${g.rows || 1}`, borderRadius: 'var(--cv-radius)' }}>
                <ManagedImage
                  url={g.url}
                  basePath={g.basePath}
                  filename={g.filename}
                  alt={g.alt}
                  fit={g.fit}
                  focusX={g.focusX}
                  focusY={g.focusY}
                  className="w-full h-full transition-transform duration-[600ms] group-hover:scale-[1.04]"
                />
              </div>
            ))}
          </div>

          <div className="mt-12 bg-cv-panel flex items-center justify-between gap-6 flex-wrap" style={{ padding: '36px 40px', borderRadius: 'var(--cv-radius)' }}>
            <div className="flex flex-col gap-1">
              <p className="m-0 font-normal text-base text-cv-ink">Följ oss för daglig inspiration</p>
              <p className="m-0 text-sm text-cv-muted">Se fler projekt och bakom kulisserna-innehåll</p>
            </div>
            <div className="flex gap-3.5">
              <a href="https://www.instagram.com/chills_visuella/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-7 py-3.5 border border-cv-edge text-cv-ink no-underline text-[13px] tracking-[0.16em] uppercase rounded-cv hover:bg-cv-base transition-colors">Instagram</a>
              <a href="https://www.facebook.com/profile.php?id=61568666219208" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-7 py-3.5 border border-cv-edge text-cv-ink no-underline text-[13px] tracking-[0.16em] uppercase rounded-cv hover:bg-cv-base transition-colors">Facebook</a>
            </div>
          </div>
        </div>
      </section>

      {lightboxIndex !== null && (
        <Lightbox images={gallery} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}
    </div>
  );
};
